// Announcement Ticker — admin-managed scrolling bar.
// CSS-keyframe marquee. Duplicates messages once for seamless loop. Pauses on hover (configurable).
import { useEffect, useMemo, useState } from "react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function AnnouncementTicker() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let live = true;
    const load = () =>
      fetch(`${API}/ticker`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (live) setData(d); })
        .catch(() => {});
    load();
    // Refresh every 60s so admin changes propagate without a hard reload.
    const id = setInterval(load, 60000);
    return () => { live = false; clearInterval(id); };
  }, []);

  const settings = data?.settings || null;
  const messages = data?.messages || [];

  const styles = useMemo(() => {
    if (!settings) return null;
    return {
      bar: {
        background: settings.background,
        color: settings.foreground,
        ["--ticker-duration"]: `${settings.speed_sec}s`,
      },
      accentDot: { background: settings.accent },
    };
  }, [settings]);

  if (!settings?.enabled || messages.length === 0) return null;

  // Duplicate once so the second copy slides in as the first leaves — seamless loop.
  const doubled = [...messages, ...messages];

  return (
    <div
      data-testid="announcement-ticker"
      role="region"
      aria-label="Site announcements"
      className="relative w-full overflow-hidden border-y"
      style={{ ...styles.bar, borderColor: "rgba(255,255,255,0.10)" }}
    >
      <style>{`
        @keyframes bestrx-ticker-scroll {
          0%   { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
        .bestrx-ticker-track {
          display: inline-flex;
          gap: 0;
          will-change: transform;
          animation: bestrx-ticker-scroll var(--ticker-duration, 40s) linear infinite;
        }
        ${settings.pause_on_hover ? `.bestrx-ticker-bar:hover .bestrx-ticker-track { animation-play-state: paused; }` : ""}
        @media (prefers-reduced-motion: reduce) {
          .bestrx-ticker-track { animation-duration: 0s; animation-play-state: paused; }
        }
      `}</style>
      {/* Edge gradient masks for premium feel */}
      <div aria-hidden className="pointer-events-none absolute left-0 top-0 h-full w-12 z-10"
        style={{ background: `linear-gradient(90deg, ${settings.background}, transparent)` }} />
      <div aria-hidden className="pointer-events-none absolute right-0 top-0 h-full w-12 z-10"
        style={{ background: `linear-gradient(-90deg, ${settings.background}, transparent)` }} />

      <div className="bestrx-ticker-bar py-2.5 sm:py-3 whitespace-nowrap">
        <div className="bestrx-ticker-track" data-testid="announcement-ticker-track">
          {doubled.map((m, i) => (
            <span
              key={`${m.id}-${i}`}
              className="inline-flex items-center px-5 sm:px-6 text-[11px] sm:text-[12px] tracking-[0.22em] font-extrabold uppercase"
              data-testid={i < messages.length ? `ticker-msg-${m.id.slice(0,8)}` : undefined}
              style={m.color ? { color: m.color } : undefined}
            >
              <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full mr-4 sm:mr-5"
                style={{ background: m.color || settings.accent }} />
              {m.text}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
